import { type ReactNode, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Collapse, Stack, Tooltip, Typography } from "@mui/material";
import ReplyIcon from "@mui/icons-material/Reply";
import ReplyAllIcon from "@mui/icons-material/ReplyAll";
import ForwardIcon from "@mui/icons-material/Forward";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { errorMessage, fetchThread, type EmailThread, type ThreadMessage } from "@/api/emailThreads";
import { ReplyComposer, formatBytes, type ComposeMode } from "@/components/email/ReplyComposer";

function fmtDate(iso: string | null) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function addressList(list: { email: string; name: string | null }[]) {
  return list.map((a) => a.name || a.email).join(", ");
}

// Vendor HTML in a sandboxed iframe: no scripts (the server already strips
// them), styles can't leak into the dashboard, and links open in a new tab.
// allow-same-origin (safe without allow-scripts) lets us size the frame to its
// content and lets cid: images load from our attachment endpoint with the
// session cookie.
function HtmlFrame({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);

  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank">
<style>body{margin:0;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#252f38;word-wrap:break-word}
img{max-width:100%;height:auto}table{max-width:100%}blockquote{margin:0 0 0 .8ex;border-left:1px solid #ccc;padding-left:1ex}
#ap-msg{display:flow-root}</style>
</head><body><div id="ap-msg">${html}</div></body></html>`;

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return undefined;
    const resize = () => {
      // Measure the content, not the document: the document is at least as
      // tall as the frame, so sizing the frame from it only ever grows.
      const content = frame.contentDocument?.getElementById("ap-msg");
      if (content) setHeight(Math.min(Math.max(Math.ceil(content.getBoundingClientRect().height) + 4, 24), 4000));
    };
    frame.addEventListener("load", resize);
    // Images arrive after load; re-measure as they do.
    const timer = window.setInterval(resize, 500);
    const stop = window.setTimeout(() => window.clearInterval(timer), 5000);
    return () => {
      frame.removeEventListener("load", resize);
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
  }, [html]);

  return (
    <Box
      component="iframe"
      ref={ref}
      title="Email message"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      sx={{ width: "100%", height, border: 0, display: "block" }}
    />
  );
}

function MessageCard({ msg, expanded, onToggle, onCompose, canReply }: {
  msg: ThreadMessage;
  expanded: boolean;
  onToggle: () => void;
  onCompose: (mode: ComposeMode) => void;
  canReply: boolean;
}) {
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper", overflow: "hidden" }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
        sx={{ px: 1.5, py: 1, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
      >
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
            {msg.from_name || msg.from_address}
          </Typography>
          {msg.portal_reply && (
            <Tooltip title={`Sent from the portal as ${msg.portal_reply.sent_as}`}>
              <Chip size="small" color="primary" variant="outlined" label={`Sent by ${msg.portal_reply.sent_by}`} />
            </Tooltip>
          )}
          {msg.attachments.length > 0 && <AttachFileIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>{fmtDate(msg.date)}</Typography>
        </Stack>
        {expanded ? (
          <Typography variant="caption" color="text.secondary" component="div">
            to {addressList(msg.to)}{msg.cc.length > 0 ? ` · cc ${addressList(msg.cc)}` : ""}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" noWrap>{msg.snippet}</Typography>
        )}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {msg.body_html ? (
            <HtmlFrame html={msg.body_html} />
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
              {msg.body_text?.trim() || "(no text content)"}
            </Typography>
          )}

          {msg.attachments.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.5 }}>
              {msg.attachments.map((a) => (
                <Chip
                  key={a.part_id}
                  size="small"
                  variant="outlined"
                  clickable
                  component="a"
                  href={a.url}
                  target="_blank"
                  rel="noopener"
                  icon={(a.mime_type ?? "").includes("pdf") ? <PictureAsPdfIcon /> : <AttachFileIcon />}
                  label={`${a.filename}${a.size ? ` · ${formatBytes(a.size)}` : ""}`}
                />
              ))}
            </Stack>
          )}

          {canReply && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button size="small" variant="outlined" startIcon={<ReplyIcon />} onClick={() => onCompose("reply")}>Reply</Button>
              <Button size="small" variant="outlined" startIcon={<ReplyAllIcon />} onClick={() => onCompose("reply_all")}>Reply all</Button>
              <Button size="small" variant="outlined" startIcon={<ForwardIcon />} onClick={() => onCompose("forward")}>Forward</Button>
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// The whole Gmail thread of a triaged email, oldest first. Older messages are
// collapsed to a line; the newest is open. `fallback` (the stored first
// message) shows if Gmail can't be reached.
// intent: open the composer on the newest message as soon as the thread loads
// (from the queue's right-click menu); onIntentDone clears it so it runs once.
export function ThreadView({ emailId, fallback, intent, onIntentDone }: {
  emailId: number;
  fallback: ReactNode;
  intent?: ComposeMode | null;
  onIntentDone?: () => void;
}) {
  const { user } = useAuth();
  const thread = useQuery<EmailThread>({
    queryKey: ["email-thread", emailId],
    queryFn: () => fetchThread(emailId),
    staleTime: 30_000,
  });
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [compose, setCompose] = useState<{ message: ThreadMessage; mode: ComposeMode } | null>(null);

  // Open the newest message whenever the thread (re)loads with a new one.
  const newestId = thread.data?.messages[thread.data.messages.length - 1]?.id;
  useEffect(() => {
    if (newestId) setOpen((prev) => new Set(prev).add(newestId));
  }, [newestId]);

  useEffect(() => {
    if (!intent || !thread.data) return;
    const latest = thread.data.messages[thread.data.messages.length - 1];
    if (latest && user?.email_responder) setCompose({ message: latest, mode: intent });
    onIntentDone?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, thread.data]);

  if (thread.isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>;
  }
  if (thread.isError || !thread.data) {
    return (
      <Stack spacing={1}>
        <Alert severity="warning">Couldn't load the full thread from Gmail ({errorMessage(thread.error)}). Showing the first message.</Alert>
        {fallback}
      </Stack>
    );
  }

  const messages = thread.data.messages;
  const newest = messages[messages.length - 1];
  const allOpen = messages.length > 0 && messages.every((m) => open.has(m.id));
  const toggle = (id: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Email thread · {messages.length} message{messages.length === 1 ? "" : "s"}
        </Typography>
        {messages.length > 1 && (
          <Button size="small" onClick={() => setOpen(allOpen ? new Set(newestId ? [newestId] : []) : new Set(messages.map((m) => m.id)))}>
            {allOpen ? "Collapse all" : "Expand all"}
          </Button>
        )}
      </Stack>
      <Stack spacing={0.75}>
        {messages.map((m) => (
          <MessageCard
            key={m.id}
            msg={m}
            expanded={open.has(m.id)}
            onToggle={() => toggle(m.id)}
            onCompose={(mode) => setCompose({ message: m, mode })}
            canReply={!!user?.email_responder}
          />
        ))}
      </Stack>
      {/* Always-visible reply bar for the newest message, so replying doesn't
          depend on finding the buttons inside an expanded message. */}
      {newest && (user?.email_responder ? (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button variant="contained" startIcon={<ReplyIcon />} onClick={() => setCompose({ message: newest, mode: "reply" })}>Reply</Button>
          <Button variant="outlined" startIcon={<ReplyAllIcon />} onClick={() => setCompose({ message: newest, mode: "reply_all" })}>Reply all</Button>
          <Button variant="outlined" startIcon={<ForwardIcon />} onClick={() => setCompose({ message: newest, mode: "forward" })}>Forward</Button>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          To reply from here, ask an admin to turn on Email replies for you on the Users page.
        </Typography>
      ))}
      {compose && (
        <ReplyComposer
          emailId={emailId}
          thread={thread.data}
          message={compose.message}
          mode={compose.mode}
          onClose={() => setCompose(null)}
        />
      )}
    </Box>
  );
}
